import React, {useState, useEffect, useCallback} from 'react';
import {Button, Card as BpCard, Collapse, H5, Icon, Popover, Spinner, Tag} from "@blueprintjs/core";
import styled from "styled-components";

import {SearchableDataGrid} from "/src/components/tables/data-grid";
import {scheduledReportJobs} from "/src/services/api";
import {DASHBOARD_APP_BASE} from "/src/config/vars";
import {ScheduleDialog} from "./schedule-dialog";

const ReportsCard = styled(BpCard)`
    padding: 12px;
    border-radius: 20px;
    background: white !important;
    &[class*="bp"][class*="-card"] {
        box-shadow: 0 0 0 1px rgba(17, 20, 24, 0.1),
                    0 1px 1px rgba(17, 20, 24, 0.1),
                    0 2px 6px rgba(17, 20, 24, 0.1);
    }
`;

const ReportsHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    user-select: none;
`;

const EmailList = styled.div`
    padding: 8px 12px;
    font-size: 13px;
    max-width: 320px;
    div { padding: 2px 0; }
`;

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const JOB_TYPE_LABELS = {
    dashboardReportToImage: 'Report',
    csvExtraction: 'CSV',
};

const formatHour = (h) => {
    if (h === 0) return '12am';
    if (h === 12) return '12pm';
    return h > 12 ? `${h - 12}pm` : `${h}am`;
};

const formatSchedule = (schedule) => {
    if (!schedule?.cadence || schedule.cadence === 'none') return '—';
    const hour = formatHour(schedule.sendHour ?? 0);
    if (schedule.cadence === 'hourly') return 'Hourly';
    if (schedule.cadence === 'daily') return `Daily at ${hour}`;
    if (schedule.cadence === 'weekly') return `Weekly on ${DAY_NAMES[schedule.sendDay] || 'day ' + schedule.sendDay} at ${hour}`;
    if (schedule.cadence === 'monthly') return `Monthly on the ${schedule.sendDay}${schedule.sendDay === 1 ? 'st' : schedule.sendDay === 2 ? 'nd' : schedule.sendDay === 3 ? 'rd' : 'th'} at ${hour}`;
    return schedule.cadence;
};

const DistributionCell = ({row}) => {
    const d = row.distribution;
    if (d?.sendType === 'email - all org users') {
        return <Tag minimal round icon="people">All users</Tag>;
    }
    const emails = d?.toEmails?.filter(Boolean) || [];
    if (!emails.length) return '—';
    return (
        <Popover
            minimal
            position="bottom"
            interactionKind="hover"
            hoverOpenDelay={200}
            hoverCloseDelay={100}
            content={
                <EmailList>
                    {emails.map(e => <div key={e}>{e}</div>)}
                </EmailList>
            }
        >
            <Tag minimal round interactive icon="envelope">
                {emails.length} recipient{emails.length !== 1 ? 's' : ''}
            </Tag>
        </Popover>
    );
};

const DetailsCell = ({row}) => {
    const imp = encodeURIComponent(row.impersonationAuth0Id);
    if (row.dashboardReportToImage) {
        const reportId = row.dashboardReportToImage.dashboardReportId;
        const locs = row.dashboardReportToImage.restrictToLocations?.length;
        const href = `${DASHBOARD_APP_BASE}/reports/${reportId}?impersonateId=${imp}`;
        return (
            <span style={{fontSize: 12}}>
                <a href={href} target="_blank" rel="noopener noreferrer">{reportId}</a>
                {locs > 0 && <span style={{color: '#5c7080'}}> · {locs} loc{locs !== 1 ? 's' : ''}</span>}
            </span>
        );
    }
    if (row.csvExtraction) {
        const path = row.csvExtraction.dashboardPath || '';
        const [basePath, hash] = path.split('#');
        const href = `${DASHBOARD_APP_BASE}${basePath}${hash ? '#' + hash : ''}&impersonateId=${imp}`;
        return (
            <span style={{fontSize: 12}}>
                <a href={href} target="_blank" rel="noopener noreferrer">{basePath || 'CSV'}</a>
            </span>
        );
    }
    return '—';
};

const makeColumns = (onEdit) => [
    {
        key: 'id',
        name: 'ID',
        width: 220,
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: ({row}) => (
            <div style={{display: 'flex', alignItems: 'center', gap: 6}}>
                <Icon
                    icon="edit"
                    size={14}
                    style={{cursor: 'pointer', color: '#5c7080', flexShrink: 0}}
                    onClick={(e) => { e.stopPropagation(); onEdit(row); }}
                />
                <span style={{overflow: 'hidden', textOverflow: 'ellipsis'}}>{row.id}</span>
            </div>
        ),
    },
    {
        key: 'jobType',
        name: 'Type',
        width: 80,
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: ({row}) => (
            <Tag minimal round small intent="primary">
                {JOB_TYPE_LABELS[row.jobType] || row.jobType}
            </Tag>
        ),
    },
    {
        key: 'scheduleText',
        name: 'Schedule',
        resizable: true,
        sortable: true,
        filterable: true,
    },
    {
        key: 'distributionText',
        name: 'Distribution',
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: DistributionCell,
    },
    {
        key: 'enabled',
        name: 'Status',
        width: 90,
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: ({row}) => (
            <Tag minimal intent={row.scheduledSendingDisabled ? 'warning' : 'success'}>
                {row.scheduledSendingDisabled ? 'Disabled' : 'Active'}
            </Tag>
        ),
    },
    {
        key: 'details',
        name: 'Details',
        resizable: true,
        sortable: true,
        filterable: true,
        renderCell: DetailsCell,
    },
    {
        key: 'impersonationAuth0Id',
        name: 'Impersonating',
        resizable: true,
        sortable: true,
        filterable: true,
    },
];

const prepareRow = (r) => ({
    ...r,
    scheduleText: formatSchedule(r.schedule),
    distributionText: r.distribution?.sendType === 'email - all org users'
        ? 'All users'
        : (r.distribution?.toEmails?.filter(Boolean) || []).join(', '),
    enabled: r.scheduledSendingDisabled ? 'Disabled' : 'Active',
    details: r.dashboardReportToImage?.dashboardReportId
        || r.csvExtraction?.dashboardPath?.split('#')[0]
        || '',
});

const ScheduledReports = ({orgid}) => {
    const [collapsed, setCollapsed] = useState(true);
    const [reports, setReports] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingJob, setEditingJob] = useState(null);

    const loadReports = useCallback(() => {
        scheduledReportJobs.list().then(all => {
            setReports(all.filter(r => r.organisation === orgid).map(prepareRow));
            setLoaded(true);
        }).catch(err => {
            console.log("Failed to load scheduled reports: " + err.message);
            setReports([]);
            setLoaded(true);
        });
    }, [orgid]);

    useEffect(() => {
        if (collapsed || loaded) return;
        loadReports();
    }, [collapsed, loaded, loadReports]);

    const handleEdit = useCallback((row) => {
        setEditingJob(row);
        setDialogOpen(true);
    }, []);

    const handleCreate = useCallback((e) => {
        e.stopPropagation();
        setEditingJob(null);
        setDialogOpen(true);
    }, []);

    const handleSaved = useCallback(() => {
        loadReports();
    }, [loadReports]);

    const columns = makeColumns(handleEdit);

    return (
        <ReportsCard>
            <ReportsHeader onClick={() => setCollapsed(c => !c)}>
                <Icon icon={collapsed ? "caret-right" : "caret-down"} style={{color: '#5c7080'}} />
                <H5 style={{margin: 0}}>Scheduled Reports</H5>
                {loaded && reports && (
                    <Tag minimal round>{reports.length}</Tag>
                )}
                {!collapsed && (
                    <Button
                        small
                        minimal
                        icon="add"
                        intent="primary"
                        onClick={handleCreate}
                        style={{marginLeft: 'auto'}}
                    />
                )}
            </ReportsHeader>
            <Collapse isOpen={!collapsed}>
                <div style={{paddingTop: '0.5em'}}>
                    {!loaded ? (
                        <Spinner size={20} />
                    ) : !reports.length ? (
                        <Tag minimal icon="info-sign">No scheduled reports for this organisation</Tag>
                    ) : (
                        <SearchableDataGrid
                            columns={columns}
                            data={reports}
                            defaultSort="scheduleText"
                            fitHeight
                        />
                    )}
                </div>
            </Collapse>
            <ScheduleDialog
                isOpen={dialogOpen}
                onClose={() => setDialogOpen(false)}
                orgid={orgid}
                job={editingJob}
                onSaved={handleSaved}
            />
        </ReportsCard>
    );
};

export {ScheduledReports};
